import React from "react";
import { Package, Tag, BookOpen, Box, FileText, Globe } from "lucide-react";

const ApplicationsSection = () => {
  const applications = [
    {
      icon: Package,
      title: "Packaging",
      description: "Premium coatings for food packaging, consumer goods, and luxury packaging with excellent barrier properties.",
      examples: ["Food Packaging", "Consumer Goods", "Luxury Items"]
    },
    {
      icon: Tag,
      title: "Labels & Stickers",
      description: "Durable, high-gloss coatings for pressure-sensitive labels, shrink sleeves, and promotional stickers.",
      examples: ["Pressure Sensitive", "Shrink Sleeves", "Promotional"]
    },
    {
      icon: BookOpen,
      title: "Publishing",
      description: "Superior finish for book covers, magazines, catalogs, and commercial print materials.",
      examples: ["Book Covers", "Magazines", "Catalogs"]
    },
    {
      icon: Box,
      title: "Specialty Board",
      description: "High-performance coatings for carton board, folding cartons, and corrugated packaging solutions.",
      examples: ["Carton Board", "Folding Cartons", "Corrugated"]
    },
    {
      icon: FileText,
      title: "Flexible Packaging",
      description: "Advanced coatings for flexible films, pouches, and multilayer packaging structures.",
      examples: ["Flexible Films", "Pouches", "Multilayer"]
    },
    {
      icon: Globe,
      title: "Export Markets",
      description: "Serving international clients across Vietnam, Dubai, and Sri Lanka with reliable UV & LED coating solutions.",
      examples: ["Vietnam", "Dubai", "Sri Lanka"]
    }
  ];

  return (
    <section id="applications" className="section-padding bg-white reveal">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#8B1538] font-semibold text-sm uppercase tracking-wider">Real-World Impact</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Application Areas
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Our UV and LED curable coatings deliver exceptional results across diverse industries and applications, from domestic markets to international exports.
          </p>
        </div>

        {/* Applications Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {applications.map((app, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-2xl hover:border-[#8B1538] transition-all duration-300 group"
            >
              <div className="bg-gradient-to-br from-red-50 to-red-100 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#8B1538] group-hover:to-[#DC143C] transition-all">
                <app.icon className="h-8 w-8 text-[#8B1538] group-hover:text-white transition-colors" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{app.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">{app.description}</p>
              
              <div className="flex flex-wrap gap-2">
                {app.examples.map((example, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-semibold bg-red-50 text-[#8B1538] px-3 py-1 rounded-full border border-red-100"
                  >
                    {example}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Global Reach Banner */}
        <div className="mt-16 bg-gradient-to-r from-[#8B1538] to-[#DC143C] rounded-2xl p-8 lg:p-12 text-white text-center shadow-2xl">
          <Globe className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h3 className="text-3xl lg:text-4xl font-bold mb-4">Global Reach, Local Expertise</h3>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Serving domestic and international markets with tailored UV & LED coating solutions. Trusted by printers, converters, and packaging companies worldwide.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-lg font-semibold">
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              🇮🇳 India - Domestic
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              🇻🇳 Vietnam - Export
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              🇦🇪 Dubai - Export
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              🇱🇰 Sri Lanka - Export
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationsSection;
